/**
 * Referral Service Client
 *
 * Provides a centralized client for all referral-related operations.
 * Replaces direct Supabase calls with API calls to the referral microservice.
 */

import { API_ENDPOINTS, getAuthHeader } from "@/config/api";
import { getAuthToken } from "./assets/assetHelpers";
import { authService } from "./auth/index";

export interface ReferralCode {
  id: string;
  user_id: string;
  code: string;
  custom_code: string | null;
  is_active: boolean;
  last_customized_at: string | null;
  created_at: string;
}

export interface ReferralStats {
  totalReferrals: number;
  qualifiedReferrals: number;
  pendingReferrals: number;
  convertedReferrals: number;
  totalMonthsEarned: number;
  monthsEarnedThisPeriod: number;
  capRemaining: number;
  capReached: boolean;
  nextResetDate: string | null;
  conversionRate: number;
}

export interface Referral {
  id: string;
  referee_id: string;
  status: string;
  referred_at: string;
  qualification_completed_at: string | null;
  qualification_deadline?: string;
  activated_at?: string;
  source?: string;
  referee_name?: string;
  referee_email?: string;
}

export interface ReferralReward {
  id: string;
  referrer_id: string;
  status: string;
  awarded_at: string;
  [key: string]: unknown;
}

export interface AttributionData {
  referralCode: string;
  source: "whatsapp" | "email" | "copy_link" | "direct";
  referrerUrl?: string;
  deviceId?: string;
  userAgent?: string;
}

export interface DeletedUserStatus {
  can_signup: boolean;
  status:
    | "available"
    | "purged"
    | "retention_expired"
    | "in_retention_period"
    | "hard_deleted_in_retention"
    | "soft_deleted_recoverable";
  message: string;
  deletion_type?: string;
  deleted_at?: string;
  retention_end_date?: string;
  days_since_deletion?: number;
  days_remaining?: number;
  can_signup_after?: string;
  recoverable?: boolean;
  note?: string;
}

/**
 * Make authenticated API call
 */
async function apiCall<T>(endpoint: string, options: globalThis.RequestInit = {}): Promise<T> {
  const token = await getAuthToken();

  const isStateChangingOperation = ["POST", "PUT", "DELETE", "PATCH"].includes(
    options.method || "GET"
  );
  const headers = isStateChangingOperation
    ? await authService.getAuthHeaders()
    : {
        ...getAuthHeader(token || undefined),
        ...options.headers,
      };

  const timeoutMs = 8000; // 8 seconds – fail fast; referral service should respond quickly
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(endpoint, {
      ...options,
      headers,
      credentials: "include",
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: "Unknown error" }));
      throw new Error(errorData.error || `API call failed: ${response.statusText}`);
    }

    return response.json();
  } catch (error: unknown) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === "AbortError") {
      throw new Error(`Request timeout after ${timeoutMs}ms`);
    }
    throw error;
  }
}

export const referralService = {
  /**
   * Get current user's referral code
   */
  async getReferralCode(): Promise<ReferralCode | null> {
    try {
      const data = await apiCall<ReferralCode | null>(API_ENDPOINTS.REFERRALS.CODE, {
        method: "GET",
      });
      return data;
    } catch (error) {
      // Handle 404 gracefully - user simply doesn't have a referral code yet
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (
        errorMessage.includes("404") ||
        errorMessage.includes("not found") ||
        errorMessage.includes("Not Found")
      ) {
        if (import.meta.env.DEV) {
          console.warn("[ReferralService] No referral code found (404) - returning null");
        }
        return null;
      }
      // Log other errors but don't throw - return null to prevent retries
      console.error("[ReferralService] getReferralCode failed:", error);
      return null;
    }
  },

  /**
   * Update custom referral code
   */
  async updateCustomCode(customCode: string): Promise<ReferralCode> {
    try {
      const data = await apiCall<ReferralCode>(API_ENDPOINTS.REFERRALS.CODE, {
        method: "PUT",
        body: JSON.stringify({ custom_code: customCode }),
      });
      return data;
    } catch (error) {
      console.error("[ReferralService] updateCustomCode failed:", error);
      throw error;
    }
  },

  /**
   * Get referral statistics
   */
  async getReferralStats(): Promise<ReferralStats> {
    try {
      const data = await apiCall<ReferralStats>(API_ENDPOINTS.REFERRALS.STATS, {
        method: "GET",
      });
      return data;
    } catch (error) {
      console.error("[ReferralService] getReferralStats failed:", error);
      throw error;
    }
  },

  /**
   * Get referral list with details
   */
  async getReferralList(): Promise<Referral[]> {
    try {
      const data = await apiCall<Referral[]>(API_ENDPOINTS.REFERRALS.LIST, {
        method: "GET",
      });
      return data;
    } catch (error) {
      console.error("[ReferralService] getReferralList failed:", error);
      throw error;
    }
  },

  /**
   * Get referral rewards history
   */
  async getReferralRewards(): Promise<ReferralReward[]> {
    try {
      const data = await apiCall<ReferralReward[]>(API_ENDPOINTS.REFERRALS.REWARDS, {
        method: "GET",
      });
      return data;
    } catch (error) {
      console.error("[ReferralService] getReferralRewards failed:", error);
      throw error;
    }
  },

  /**
   * Track referral click (public endpoint)
   */
  async trackAttribution(data: AttributionData): Promise<{
    attribution: Record<string, unknown>;
    attributionData: {
      code: string;
      referrerId: string;
      attributionId: string;
      expiresAt: string;
    };
  }> {
    try {
      // Generate device fingerprint
      const deviceId = getDeviceFingerprint();
      const { userAgent } = navigator;

      const response = await fetch(API_ENDPOINTS.REFERRALS.ATTRIBUTION_TRACK, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...data,
          deviceId,
          userAgent,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: "Unknown error" }));
        throw new Error(errorData.error || `API call failed: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error("[ReferralService] trackAttribution failed:", error);
      throw error;
    }
  },

  /**
   * Process referral after signup
   */
  async processReferral(attributionData: {
    code: string;
    referrerId: string;
    attributionId: string;
    expiresAt: string;
  }): Promise<{ referral: Record<string, unknown> } | null> {
    try {
      const data = await apiCall<{ referral: Record<string, unknown>; message?: string }>(
        API_ENDPOINTS.REFERRALS.ATTRIBUTION_PROCESS,
        {
          method: "POST",
          body: JSON.stringify({ attributionData }),
        }
      );
      return data.referral ? data : null;
    } catch (error) {
      console.error("[ReferralService] processReferral failed:", error);
      throw error;
    }
  },

  /**
   * Check if email belongs to deleted user (public endpoint)
   */
  async checkDeletedUser(email: string): Promise<DeletedUserStatus> {
    try {
      const response = await fetch(API_ENDPOINTS.REFERRALS.CHECK_DELETED_USER, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: "Unknown error" }));
        throw new Error(errorData.error || `API call failed: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      console.error("[ReferralService] checkDeletedUser failed:", error);
      throw error;
    }
  },
};

/**
 * Generate device fingerprint for fraud detection
 */
function getDeviceFingerprint(): string {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (ctx) {
    ctx.textBaseline = "top";
    ctx.font = "14px Arial";
    ctx.fillStyle = "#f60";
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = "#069";
    ctx.fillText("TellerWand", 2, 15);
    ctx.fillStyle = "rgba(102, 204, 0, 0.7)";
    ctx.fillText("Referral", 4, 17);
  }

  const canvasData = canvas.toDataURL();

  // Combine with other browser properties
  const fingerprint = [
    canvasData,
    navigator.userAgent,
    navigator.language,
    screen.colorDepth,
    `${screen.width}x${screen.height}`,
    new Date().getTimezoneOffset(),
  ].join("|");

  // Simple hash
  let hash = 0;
  for (let i = 0; i < fingerprint.length; i += 1) {
    const char = fingerprint.codePointAt(i) ?? 0;
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }

  return `device_${Math.abs(hash).toString(36)}`;
}
